<template>
  <div class="mcc-page">
    <BackButton />
    <div class="mcc-header">
      <h2>Corporate Accounts</h2>
      <p class="mcc-sub">Both sides of the wholesale relationship — the corporates you supply (My Clients), and the sellers who supply you on credit terms (On My Account With).</p>
      <button v-if="tab==='clients'" class="mcc-add-btn" @click="openAdd()"><i class="mdi mdi-plus"></i> Add Corporate Client</button>
    </div>

    <!-- Tab strip — single page, two perspectives -->
    <div class="mcc-tabs">
      <button class="mcc-tab" :class="{active: tab==='clients'}" @click="tab='clients'">
        <i class="mdi mdi-handshake-outline me-1"></i> My Clients
        <span v-if="clients.length" class="mcc-tab-badge">{{ clients.length }}</span>
      </button>
      <button class="mcc-tab" :class="{active: tab==='sellers'}" @click="tab='sellers'">
        <i class="mdi mdi-storefront-outline me-1"></i> On My Account With
        <span v-if="sellers.length" class="mcc-tab-badge">{{ sellers.length }}</span>
      </button>
    </div>

    <!-- ====== TAB: My Clients ====== -->
    <template v-if="tab === 'clients'">
    <div v-if="loading" class="mcc-empty"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>
    <div v-else-if="!clients.length" class="mcc-empty">
      <i class="mdi mdi-domain"></i>
      <h3>No corporate clients yet</h3>
      <p>Add your first client — once they're on your list, you can invoice them after every slaughter without retyping their details.</p>
      <button class="mcc-add-btn" @click="openAdd()">Add your first client</button>
    </div>

    <div v-else class="mcc-grid">
      <div v-for="c in clients" :key="c.id" class="mcc-card" :class="{suspended: c.status==='suspended'}">
        <div class="mcc-card-top">
          <div class="mcc-logo">
            <img v-if="c.logo_url" :src="apiBase + '/' + c.logo_url" :alt="c.company_name">
            <i v-else class="mdi mdi-domain"></i>
          </div>
          <div class="mcc-card-id">
            <div class="mcc-name">{{ c.company_name }}</div>
            <div class="mcc-type">{{ formatBiz(c.business_type) }}</div>
          </div>
          <div class="mcc-actions">
            <button class="mcc-icon-btn" @click="openEdit(c)" title="Edit"><i class="mdi mdi-pencil"></i></button>
            <button class="mcc-icon-btn" @click="archive(c)" title="Archive"><i class="mdi mdi-archive-arrow-down-outline"></i></button>
          </div>
        </div>
        <div class="mcc-meta">
          <div><i class="mdi mdi-cash-multiple"></i> {{ c.currency || 'KES' }} {{ Number(c.flat_rate_per_kg||0).toLocaleString() }}/kg ({{ basisLabel(c.billing_weight_basis) }})</div>
          <div><i class="mdi mdi-calendar-clock"></i> {{ c.payment_terms_days }}-day terms · {{ formatMethod(c.payment_method) }}</div>
          <div v-if="c.credit_limit_kes"><i class="mdi mdi-bank-outline"></i> Credit limit: KES {{ Number(c.credit_limit_kes).toLocaleString() }}</div>
        </div>
        <div class="mcc-stats">
          <div class="mcc-stat"><span class="lbl">Outstanding</span><span class="val" :class="{warn:Number(c.outstanding_kes)>0}">KES {{ Number(c.outstanding_kes||0).toLocaleString() }}</span></div>
          <div class="mcc-stat"><span class="lbl">Open invoices</span><span class="val">{{ c.open_invoices||0 }}</span></div>
          <div class="mcc-stat"><span class="lbl">Overdue</span><span class="val" :class="{warn:Number(c.overdue_invoices)>0}">{{ c.overdue_invoices||0 }}</span></div>
          <div class="mcc-stat"><span class="lbl">YTD billed</span><span class="val">KES {{ Number(c.ytd_billed_kes||0).toLocaleString() }}</span></div>
        </div>
        <div class="mcc-card-foot">
          <router-link :to="'/receivables?client='+c.id" class="mcc-link">View receivables →</router-link>
        </div>
      </div>
    </div>
    </template>

    <!-- ====== TAB: On My Account With ====== -->
    <template v-if="tab === 'sellers'">
    <div v-if="loadingSellers" class="mcc-empty"><i class="mdi mdi-loading mdi-spin"></i> Loading…</div>
    <div v-else-if="!sellers.length" class="mcc-empty">
      <i class="mdi mdi-storefront-outline"></i>
      <h3>No sellers have set you up yet</h3>
      <p>When a seller adds your company as a corporate client, they'll appear here with their agreed payment terms. You can then use "On account" in checkout for their items.</p>
    </div>
    <div v-else class="mcc-grid">
      <div v-for="s in sellers" :key="s.relationship_id" class="mcc-card">
        <div class="mcc-card-top">
          <div class="mcc-logo">
            <img v-if="s.seller_avatar" :src="avatarUrl(s.seller_avatar)" :alt="s.seller_name">
            <i v-else class="mdi mdi-account"></i>
          </div>
          <div class="mcc-card-id">
            <div class="mcc-name">{{ s.seller_name || 'Seller' }}</div>
            <div class="mcc-type">You're listed as <strong>{{ s.my_company_name }}</strong></div>
          </div>
        </div>
        <div class="mcc-meta">
          <div><i class="mdi mdi-calendar-clock"></i> {{ s.payment_terms_days }}-day {{ formatMethod(s.payment_method) }}</div>
          <div v-if="s.credit_limit_kes"><i class="mdi mdi-bank-outline"></i> Credit limit: KES {{ Number(s.credit_limit_kes).toLocaleString() }}</div>
          <div v-if="Number(s.purchase_order_required)===1"><i class="mdi mdi-clipboard-text-outline"></i> PO number required on every order</div>
        </div>
        <div class="mcc-stats">
          <div class="mcc-stat"><span class="lbl">Outstanding</span><span class="val" :class="{warn:Number(s.outstanding_kes)>0}">KES {{ Number(s.outstanding_kes||0).toLocaleString() }}</span></div>
          <div class="mcc-stat"><span class="lbl">Open invoices</span><span class="val">{{ s.open_invoices||0 }}</span></div>
          <div class="mcc-stat"><span class="lbl">Overdue</span><span class="val" :class="{warn:Number(s.overdue_invoices)>0}">{{ s.overdue_invoices||0 }}</span></div>
          <div class="mcc-stat"><span class="lbl">YTD billed</span><span class="val">KES {{ Number(s.ytd_billed_kes||0).toLocaleString() }}</span></div>
        </div>
        <div class="mcc-card-foot">
          <router-link :to="'/u/'+s.seller_user_id" class="mcc-link">View {{ s.seller_name }}'s profile →</router-link>
        </div>
      </div>
    </div>
    </template>

    <!-- ADD / EDIT MODAL -->
    <div v-if="modalOpen" class="mcc-modal-bg" @click.self="closeModal()">
      <div class="mcc-modal">
        <div class="mcc-modal-head">
          <h3>{{ form.clientId ? 'Edit ' + (form._companyName || 'Corporate Client') : 'Add Corporate Client' }}</h3>
          <button class="mcc-close" @click="closeModal()"><i class="mdi mdi-close"></i></button>
        </div>

        <!-- STEP 1 — company picker / details -->
        <div v-if="!form.clientId" class="mcc-section">
          <label class="mcc-lbl">Search for an existing company first (by name or KRA PIN)</label>
          <div class="mcc-search-wrap">
            <input v-model="searchTerm" @input="onSearch" placeholder="Type at least 2 characters — e.g. Choice Meats or KRA PIN" class="mcc-input">
            <div v-if="searchResults.length" class="mcc-search-dropdown">
              <div v-for="r in searchResults" :key="r.id" class="mcc-search-item" @click="pickExisting(r)">
                <div class="mcc-search-name">{{ r.company_name }}</div>
                <div class="mcc-search-sub">{{ formatBiz(r.business_type) }} · {{ r.contact_phone || '—' }} · KRA {{ r.kra_pin || '—' }}</div>
              </div>
            </div>
          </div>
          <div class="mcc-or">— or fill in fresh —</div>

          <div class="mcc-grid-2">
            <div class="mcc-field"><label>Company name *</label><input v-model="form.companyName" class="mcc-input"></div>
            <div class="mcc-field"><label>Business type</label>
              <select v-model="form.businessType" class="mcc-input">
                <option value="butchery">Butchery</option>
                <option value="processor">Meat processor</option>
                <option value="exporter">Exporter</option>
                <option value="wholesaler">Wholesaler / distributor</option>
                <option value="retailer">Retailer</option>
                <option value="restaurant">Restaurant / hotel chain</option>
                <option value="feedlot">Feedlot / fattener</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="mcc-field"><label>Contact person</label><input v-model="form.contactName" class="mcc-input"></div>
            <div class="mcc-field"><label>Contact phone *</label><input v-model="form.contactPhone" class="mcc-input"></div>
            <div class="mcc-field"><label>Contact email</label><input v-model="form.contactEmail" class="mcc-input"></div>
            <div class="mcc-field"><label>KRA PIN</label><input v-model="form.kraPin" class="mcc-input"></div>
            <div class="mcc-field"><label>Reg. No.</label><input v-model="form.companyRegNo" class="mcc-input"></div>
            <div class="mcc-field full"><label>Address</label><input v-model="form.address" class="mcc-input"></div>
          </div>
        </div>

        <!-- STEP 2 — pricing & billing -->
        <h4 class="mcc-h4">Pricing &amp; billing</h4>
        <div class="mcc-grid-2">
          <div class="mcc-field"><label>Billing weight basis</label>
            <select v-model="form.billingWeightBasis" class="mcc-input">
              <option value="hot">Hot carcass weight</option>
              <option value="cold">Cold carcass weight</option>
              <option value="live">Live weight</option>
            </select>
            <small class="mcc-hint">Choice Meats / KMC = hot. Some butcheries prefer cold (after chilling). Feedlots = live.</small>
          </div>
          <div class="mcc-field"><label>Pricing mode</label>
            <select v-model="form.pricingMode" class="mcc-input">
              <option value="flat">Flat rate per kg</option>
              <option value="by_grade">Different rates by grade (A/B/C)</option>
              <option value="live_per_kg">Live per kg</option>
            </select>
          </div>
          <div v-if="form.pricingMode!=='by_grade'" class="mcc-field"><label>Rate KES/kg</label><input v-model.number="form.flatRatePerKg" type="number" min="0" class="mcc-input"></div>
          <div v-else class="mcc-field full">
            <label>Grade rates (KES/kg)</label>
            <div class="mcc-grade-row">
              <div v-for="g in ['A','B','C']" :key="g" class="mcc-grade-cell">
                <span class="mcc-grade-tag">{{ g }}</span>
                <input v-model.number="form.gradeRates[g]" type="number" min="0" class="mcc-input">
              </div>
            </div>
          </div>
          <div class="mcc-field"><label>Condemnation handling</label>
            <select v-model="form.condemnationBilling" class="mcc-input">
              <option value="net_of_condemned">Bill net of condemned parts</option>
              <option value="full_hot">Bill full carcass (buyer's risk)</option>
            </select>
          </div>
          <div class="mcc-field"><label>Delivery terms</label>
            <select v-model="form.deliveryTerms" class="mcc-input">
              <option value="ex_farm">Ex-farm (buyer collects)</option>
              <option value="delivered">Delivered to buyer</option>
            </select>
          </div>
        </div>

        <h4 class="mcc-h4">Payment</h4>
        <div class="mcc-grid-2">
          <div class="mcc-field"><label>Payment terms (days)</label><input v-model.number="form.paymentTermsDays" type="number" min="0" class="mcc-input"><small class="mcc-hint">0 = cash on delivery. Choice Meats typical = 4. Hotels = 30.</small></div>
          <div class="mcc-field"><label>Payment method</label>
            <select v-model="form.paymentMethod" class="mcc-input">
              <option value="rtgs">RTGS</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="mpesa_stk">M-Pesa STK push</option>
              <option value="mpesa_paybill">M-Pesa paybill</option>
              <option value="cheque">Cheque</option>
              <option value="cash">Cash</option>
            </select>
          </div>
          <div class="mcc-field"><label>Credit limit (KES)</label><input v-model.number="form.creditLimitKes" type="number" min="0" class="mcc-input"><small class="mcc-hint">Blank = unlimited. Used by Pay-on-Account gate.</small></div>
          <div class="mcc-field"><label>Deposit required (%)</label><input v-model.number="form.depositRequiredPct" type="number" min="0" max="100" class="mcc-input"><small class="mcc-hint">e.g. 50 = collect half before slaughter.</small></div>
          <div class="mcc-field"><label>Partial payments allowed?</label>
            <select v-model="form.partialPaymentsAllowed" class="mcc-input">
              <option :value="1">Yes — corporate can pay in chunks</option>
              <option :value="0">No — full settlement only</option>
            </select>
          </div>
          <div class="mcc-field"><label>Purchase order required?</label>
            <select v-model="form.purchaseOrderRequired" class="mcc-input">
              <option :value="0">No</option>
              <option :value="1">Yes — block invoice until PO entered</option>
            </select>
          </div>
        </div>

        <h4 class="mcc-h4">Invoicing</h4>
        <div class="mcc-grid-2">
          <div class="mcc-field"><label>Consolidation rule</label>
            <select v-model="form.invoiceConsolidation" class="mcc-input">
              <option value="per_carcass">One invoice per carcass (export/halal)</option>
              <option value="per_day">Bundle same-day carcasses (default)</option>
              <option value="per_week">Weekly statement</option>
              <option value="manual">Manual — I'll pick each time</option>
            </select>
          </div>
          <div class="mcc-field"><label>Invoice prefix</label><input v-model="form.invoicePrefix" placeholder="e.g. CC" maxlength="6" class="mcc-input"><small class="mcc-hint">Optional. Default: INV. e.g. "CC" → CC-2026-00042</small></div>
          <div class="mcc-field"><label>VAT treatment</label>
            <select v-model="form.vatTreatment" class="mcc-input">
              <option value="zero_rated">Zero-rated (most meat)</option>
              <option value="standard_16">Standard 16% VAT</option>
              <option value="exempt">Exempt</option>
            </select>
          </div>
          <div class="mcc-field"><label>WHT rate (%)</label><input v-model.number="form.whtRatePct" type="number" min="0" max="20" step="0.5" class="mcc-input"></div>
        </div>

        <h4 class="mcc-h4">Reminders</h4>
        <div class="mcc-grid-2">
          <div class="mcc-field"><label>Reminder days (negative=before due)</label><input v-model="reminderText" placeholder="e.g. -1, 1, 3, 7" class="mcc-input"><small class="mcc-hint">Comma-separated offsets from due date. Defaults to <code>1, 3, 7</code>.</small></div>
          <div class="mcc-field"><label>Reminder channels</label>
            <select v-model="form.reminderChannels" class="mcc-input">
              <option value="both">SMS + Email</option>
              <option value="email">Email only</option>
              <option value="sms">SMS only</option>
            </select>
          </div>
        </div>

        <div class="mcc-field full">
          <label>Notes (internal)</label>
          <textarea v-model="form.notes" rows="2" class="mcc-input"></textarea>
        </div>

        <div class="mcc-modal-foot">
          <button class="mcc-secondary" @click="closeModal()">Cancel</button>
          <button class="mcc-primary" :disabled="saving" @click="save()"><span v-if="saving"><i class="mdi mdi-loading mdi-spin"></i> Saving…</span><span v-else>{{ form.clientId ? 'Save changes' : 'Add client &amp; notify them' }}</span></button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { executeJson, flocker, uploads } from "@/api";
import BackButton from "@/components/BackButton.vue";
export default {
  name: "MyCorporateClients",
  components: { BackButton },
  data(){return{
    // 'clients' = the corporates I (as a seller) supply.
    // 'sellers' = the sellers who've added me/my corporates as their client.
    tab: 'clients',
    clients: [],
    sellers: [],
    loading: true,
    loadingSellers: false,
    modalOpen: false,
    saving: false,
    searchTerm: "",
    searchResults: [],
    searchTimer: null,
    reminderText: "1, 3, 7",
    form: this.emptyForm(),
    apiBase: process.env.VUE_APP_API_BASE || "",
  }},
  watch: {
    tab(v){ if (v === 'sellers' && !this.sellers.length && !this.loadingSellers) this.loadSellers(); }
  },
  mounted(){ this.load(); this.loadSellers(); },
  methods:{
    emptyForm(){
      return {
        clientId: "", _companyName: "",
        corporateId: 0,
        companyName: "", businessType: "processor",
        contactName: "", contactPhone: "", contactEmail: "",
        kraPin: "", companyRegNo: "", address: "",
        billingWeightBasis: "hot", pricingMode: "flat",
        flatRatePerKg: 0, gradeRates: { A: 0, B: 0, C: 0 },
        condemnationBilling: "net_of_condemned", deliveryTerms: "ex_farm",
        paymentTermsDays: 4, paymentMethod: "rtgs",
        creditLimitKes: null, depositRequiredPct: 0,
        partialPaymentsAllowed: 1, purchaseOrderRequired: 0,
        invoiceConsolidation: "per_day", invoicePrefix: "",
        vatTreatment: "zero_rated", whtRatePct: 0,
        reminderChannels: "both",
        notes: "",
      };
    },
    async load(){
      this.loading = true;
      try {
        const r = await executeJson({ function: "getMyCorporateClients", sellerId: sessionStorage.getItem("userId") }, flocker);
        if (r.data && r.data.success) this.clients = r.data.data.clients || [];
      } catch (e){ /* silent */ }
      this.loading = false;
    },
    async loadSellers(){
      this.loadingSellers = true;
      try {
        const r = await executeJson({ function: "getSellersWithMeAsCorporate", userId: sessionStorage.getItem("userId") }, flocker);
        if (r.data && r.data.success) this.sellers = r.data.data.sellers || [];
      } catch (e){ /* silent */ }
      this.loadingSellers = false;
    },
    avatarUrl(u){
      if (!u) return '';
      var s = String(u);
      if (s.indexOf('http') === 0) return s;
      return uploads + s.replace(/^uploads\//, '');
    },
    openAdd(){
      this.form = this.emptyForm();
      this.reminderText = "1, 3, 7";
      this.searchTerm = ""; this.searchResults = [];
      this.modalOpen = true;
    },
    openEdit(c){
      this.form = {
        clientId: c.id, _companyName: c.company_name,
        corporateId: c.corporate_account_id,
        companyName: c.company_name, businessType: c.business_type,
        contactName: "", contactPhone: c.contact_phone || "", contactEmail: c.contact_email || "",
        kraPin: c.kra_pin || "", companyRegNo: "", address: "",
        billingWeightBasis: c.billing_weight_basis,
        pricingMode: c.pricing_mode,
        flatRatePerKg: Number(c.flat_rate_per_kg||0),
        gradeRates: Object.assign({ A:0, B:0, C:0 }, c.grade_rates || {}),
        condemnationBilling: c.condemnation_billing,
        deliveryTerms: c.delivery_terms,
        paymentTermsDays: c.payment_terms_days,
        paymentMethod: c.payment_method,
        creditLimitKes: c.credit_limit_kes,
        depositRequiredPct: c.deposit_required_pct,
        partialPaymentsAllowed: Number(c.partial_payments_allowed),
        purchaseOrderRequired: Number(c.purchase_order_required),
        invoiceConsolidation: c.invoice_consolidation,
        invoicePrefix: c.invoice_prefix || "",
        vatTreatment: c.vat_treatment,
        whtRatePct: c.wht_rate_pct,
        reminderChannels: c.reminder_channels,
        notes: c.notes || "",
      };
      const sched = c.reminder_schedule || [1,3,7];
      this.reminderText = Array.isArray(sched) ? sched.join(", ") : "1, 3, 7";
      this.modalOpen = true;
    },
    closeModal(){ this.modalOpen = false; },
    onSearch(){
      clearTimeout(this.searchTimer);
      const q = this.searchTerm.trim();
      if (q.length < 2) { this.searchResults = []; return; }
      this.searchTimer = setTimeout(async () => {
        try {
          const r = await executeJson({ function: "searchCorporates", keyword: q }, flocker);
          if (r.data && r.data.success) this.searchResults = r.data.data.corporates || [];
        } catch (e){ /* silent */ }
      }, 250);
    },
    pickExisting(r){
      this.form.corporateId = r.id;
      this.form.companyName = r.company_name;
      this.form.businessType = r.business_type;
      this.form.contactPhone = r.contact_phone || "";
      this.form.contactEmail = r.contact_email || "";
      this.form.kraPin = r.kra_pin || "";
      this.form.address = r.address || "";
      this.searchResults = [];
      this.searchTerm = r.company_name;
    },
    async save(){
      this.saving = true;
      try {
        const reminderSchedule = this.reminderText.split(/[,\s]+/).map(s => parseInt(s.trim(), 10)).filter(n => !Number.isNaN(n));
        const payload = {
          sellerId: sessionStorage.getItem("userId"),
          ...this.form,
          gradeRates: JSON.stringify(this.form.gradeRates),
          reminderSchedule: JSON.stringify(reminderSchedule),
        };
        const fn = this.form.clientId ? "updateCorporateClient" : "addCorporateClient";
        const r = await executeJson({ function: fn, ...payload }, flocker);
        if (r.data && r.data.success) { this.modalOpen = false; await this.load(); }
        else alert((r.data && r.data.message) || "Save failed");
      } catch (e){ alert("Save failed: " + e.message); }
      this.saving = false;
    },
    async archive(c){
      if (!confirm("Archive " + c.company_name + "? You can re-add them later.")) return;
      try {
        const r = await executeJson({ function: "archiveCorporateClient", sellerId: sessionStorage.getItem("userId"), clientId: c.id }, flocker);
        if (r.data && r.data.success) await this.load();
        else alert((r.data && r.data.message) || "Archive failed");
      } catch (e){ /* silent */ }
    },
    formatBiz(b){
      const m = { butchery:"Butchery", processor:"Meat processor", exporter:"Exporter", wholesaler:"Wholesaler", retailer:"Retailer", restaurant:"Hotel/restaurant", feedlot:"Feedlot", other:"Other" };
      return m[b] || b;
    },
    basisLabel(b){ return ({ hot:"hot carcass", cold:"cold carcass", live:"live weight" })[b] || b; },
    formatMethod(m){ return (m||"").replace(/_/g, " "); },
  }
};
</script>

<style scoped>
.mcc-page{max-width:1200px;margin:0 auto;padding:24px 20px 80px;color:#1A1A1A}
html[data-theme="dark"] .mcc-page{color:#F5F1E8}
.mcc-header{margin:10px 0 24px}
.mcc-header h2{margin:0 0 4px;font-size:24px;letter-spacing:-0.3px}
.mcc-sub{margin:0 0 14px;color:#6B6356;font-size:13px;max-width:720px;line-height:1.5}
html[data-theme="dark"] .mcc-sub{color:#A8A095}
.mcc-add-btn{background:#BFA164;color:#1A1A1A;border:none;padding:10px 18px;border-radius:8px;font-weight:600;cursor:pointer;font-size:14px;display:inline-flex;align-items:center;gap:6px}
.mcc-tabs{display:flex;gap:8px;margin:6px 0 18px;border-bottom:1px solid #E2DCD3;padding-bottom:2px}
.mcc-tab{background:transparent;border:none;padding:10px 14px;font-size:14px;font-weight:600;color:#7A6F60;cursor:pointer;border-bottom:3px solid transparent;display:inline-flex;align-items:center;gap:4px;border-radius:6px 6px 0 0}
.mcc-tab:hover{color:#BFA164;background:rgba(191,161,100,.06)}
.mcc-tab.active{color:#1A1A1A;border-bottom-color:#BFA164;background:rgba(191,161,100,.08)}
html[data-theme="dark"] .mcc-tab{color:#A8A095}
html[data-theme="dark"] .mcc-tab.active{color:#F5F0E8}
.mcc-tab-badge{background:rgba(191,161,100,.18);color:#BFA164;border-radius:10px;padding:1px 8px;font-size:11px;font-weight:700;margin-left:4px}
.mcc-add-btn:hover{background:#A8854F}

.mcc-empty{text-align:center;padding:60px 20px;color:#8A827A}
.mcc-empty i.mdi-domain{font-size:56px;color:#BFA164;opacity:.5}
.mcc-empty h3{margin:8px 0 4px}
.mcc-empty p{max-width:380px;margin:0 auto 16px;font-size:13px}

.mcc-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:16px}
.mcc-card{background:#FFFFFF;border:1px solid #E5E0D5;border-radius:12px;padding:16px;box-shadow:0 1px 3px rgba(0,0,0,.04);transition:transform .15s, box-shadow .15s}
html[data-theme="dark"] .mcc-card{background:#1C1A16;border-color:#3A352B}
.mcc-card:hover{transform:translateY(-2px);box-shadow:0 4px 14px rgba(0,0,0,.08)}
.mcc-card.suspended{opacity:.6}
.mcc-card-top{display:flex;gap:10px;align-items:flex-start}
.mcc-logo{width:44px;height:44px;border-radius:8px;background:#F5F1E8;display:flex;align-items:center;justify-content:center;flex-shrink:0;overflow:hidden}
.mcc-logo img{width:100%;height:100%;object-fit:cover}
.mcc-logo i{font-size:24px;color:#BFA164}
html[data-theme="dark"] .mcc-logo{background:#2A2620}
.mcc-card-id{flex:1;min-width:0}
.mcc-name{font-weight:600;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mcc-type{font-size:11px;color:#8A827A;margin-top:2px;display:flex;align-items:center;gap:6px}
.mcc-pill{padding:1px 7px;border-radius:10px;font-size:9px;font-weight:700;letter-spacing:.5px}
.mcc-pill.verified{background:#1F6B1F;color:#FFF}
.mcc-pill.pending{background:#E0B040;color:#1A1A1A}
.mcc-actions{display:flex;gap:4px}
.mcc-icon-btn{background:none;border:none;color:#8A827A;cursor:pointer;padding:6px;border-radius:6px;font-size:18px}
.mcc-icon-btn:hover{background:#F5F1E8;color:#1A1A1A}
html[data-theme="dark"] .mcc-icon-btn:hover{background:#2A2620;color:#F5F1E8}

.mcc-meta{margin:12px 0 10px;font-size:12px;color:#6B6356}
html[data-theme="dark"] .mcc-meta{color:#A8A095}
.mcc-meta div{margin:3px 0;display:flex;align-items:center;gap:6px}
.mcc-meta i{color:#BFA164;font-size:14px}

.mcc-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;padding:10px;background:#FAF7EE;border-radius:8px;margin-bottom:10px}
html[data-theme="dark"] .mcc-stats{background:#252119}
.mcc-stat{display:flex;flex-direction:column;text-align:center}
.mcc-stat .lbl{font-size:9px;color:#8A827A;text-transform:uppercase;letter-spacing:.5px}
.mcc-stat .val{font-size:13px;font-weight:600}
.mcc-stat .val.warn{color:#B73E2F}

.mcc-card-foot{border-top:1px solid #F0EBE4;padding-top:10px;margin-top:6px}
html[data-theme="dark"] .mcc-card-foot{border-color:#3A352B}
.mcc-link{color:#BFA164;text-decoration:none;font-weight:600;font-size:13px}
.mcc-link:hover{color:#A8854F}

.mcc-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:flex-start;justify-content:center;z-index:2000;overflow-y:auto;padding:40px 16px}
.mcc-modal{background:#FFFFFF;width:100%;max-width:780px;border-radius:14px;padding:24px;box-shadow:0 12px 36px rgba(0,0,0,.25)}
html[data-theme="dark"] .mcc-modal{background:#1C1A16;color:#F5F1E8}
.mcc-modal-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px}
.mcc-modal-head h3{margin:0;font-size:18px}
.mcc-close{background:none;border:none;font-size:22px;color:#8A827A;cursor:pointer}
.mcc-section{margin-bottom:8px}
.mcc-h4{margin:18px 0 8px;font-size:13px;color:#8B5A2B;text-transform:uppercase;letter-spacing:1px;border-bottom:1px solid #F0EBE4;padding-bottom:4px}
html[data-theme="dark"] .mcc-h4{color:#BFA164;border-color:#3A352B}
.mcc-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.mcc-field{display:flex;flex-direction:column}
.mcc-field.full{grid-column:1/-1}
.mcc-field label{font-size:11px;color:#6B6356;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;font-weight:600}
html[data-theme="dark"] .mcc-field label{color:#A8A095}
.mcc-lbl{font-size:11px;color:#6B6356;text-transform:uppercase;letter-spacing:.5px;display:block;margin-bottom:4px;font-weight:600}
.mcc-input{padding:9px 11px;border:1px solid #D8D0C4;border-radius:6px;background:#FFFFFF;color:#1A1A1A;font-size:14px;font-family:inherit}
html[data-theme="dark"] .mcc-input{background:#0F0D0A;border-color:#3A352B;color:#F5F1E8}
.mcc-input:focus{outline:none;border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.25)}
.mcc-hint{display:block;margin-top:3px;font-size:11px;color:#8A827A}
.mcc-grade-row{display:flex;gap:8px}
.mcc-grade-cell{flex:1;display:flex;align-items:center;gap:6px}
.mcc-grade-tag{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:#BFA164;color:#1A1A1A;font-weight:700;font-size:11px;flex-shrink:0}
.mcc-search-wrap{position:relative}
.mcc-search-dropdown{position:absolute;top:100%;left:0;right:0;background:#FFFFFF;border:1px solid #D8D0C4;border-radius:6px;margin-top:2px;max-height:220px;overflow-y:auto;z-index:10;box-shadow:0 4px 12px rgba(0,0,0,.1)}
html[data-theme="dark"] .mcc-search-dropdown{background:#1C1A16;border-color:#3A352B}
.mcc-search-item{padding:8px 11px;cursor:pointer;border-bottom:1px solid #F0EBE4}
html[data-theme="dark"] .mcc-search-item{border-color:#2A2620}
.mcc-search-item:hover{background:#F5F1E8}
html[data-theme="dark"] .mcc-search-item:hover{background:#2A2620}
.mcc-search-name{font-size:14px;font-weight:600}
.mcc-search-sub{font-size:11px;color:#8A827A;margin-top:2px}
.mcc-or{text-align:center;padding:8px 0;color:#8A827A;font-size:11px;text-transform:uppercase;letter-spacing:1px}

.mcc-modal-foot{display:flex;justify-content:flex-end;gap:8px;margin-top:18px;padding-top:14px;border-top:1px solid #F0EBE4}
html[data-theme="dark"] .mcc-modal-foot{border-color:#3A352B}
.mcc-secondary{background:transparent;color:#6B6356;border:1px solid #D8D0C4;padding:9px 18px;border-radius:7px;cursor:pointer;font-weight:600}
.mcc-primary{background:#BFA164;color:#1A1A1A;border:none;padding:9px 22px;border-radius:7px;cursor:pointer;font-weight:600;display:inline-flex;align-items:center;gap:6px}
.mcc-primary:disabled{opacity:.6;cursor:not-allowed}
.mcc-primary:hover:not(:disabled){background:#A8854F}

@media (max-width:640px){
  .mcc-grid-2{grid-template-columns:1fr}
  .mcc-stats{grid-template-columns:repeat(2,1fr)}
}
</style>
